<template>
  <div class="scroll-container">
    <div class="column-selection h-100">
      <div v-for="(group, index) in filteredReportColumns" :key="group.key" class="column-group mb-4">
        <template v-if="group.source && (index === 0 || filteredReportColumns[index - 1].source !== group.source)">
          <div class="py-3 px-4 font-weight-bold" style="font-size: 16px; color: #3F3F3F;">
            {{ group.source === 'case' ? 'Case Columns' : 'Client Columns' }}
          </div>
        </template>
        <div class="column-header py-3 px-4">
          <div class="d-flex justify-content-between">
            <div class="custom-control custom-checkbox">
              <input type="checkbox" :id="'checkGroup_' + group.key" :checked="isGroupSelected(group)"
                class="custom-control-input" @change="toggleGroupSelection(group)"
                :disabled="!group.data || group.data.length === 0 || group.key === 'Questionnaire::Template'" />
              <label class="custom-control-label font-weight-bold" :for="'checkGroup_' + group.key"
                style="font-size: 16px;">
                {{ getGroupLabel(group) }}
              </label>
            </div>
            <v-menu v-if="group.key === 'Task::Dictionary'" min-width="300px" bottom :close-on-content-click="false"
              nudge-bottom="24px">
              <template v-slot:activator="{ on, attrs }">
                <div role="button" v-bind="attrs" v-on="on" class="d-flex align-items-center">
                  <span class="ma-0 mr-1" style="color: #3F3F3F; font-size: 12px;">Sort & Filter</span>
                  <v-icon small dense color="#3F3F3F">mdi-filter</v-icon>
                </div>
              </template>
              <div class="bg-white p-3">
                <div class="status">
                  <span class="mb-2 font-weight-bold d-block">Sort</span>
                  <c-select-new class="notranslate" placeholder="Latest" v-model="tempFilter.sort"
                    :options="sortOptions" item-text="text" item-value="value" />
                </div>
                <div class="status mt-4">
                  <span class="mb-2 font-weight-bold d-block">Filter - Used on procedure</span>
                  <div v-for="option in modifiedProcedureListFilter" :key="option.id" class="checkbox-status">
                    <input class="mr-2" @change="checkAll(option.id)" type="checkbox" :id="option.id" :value="option.id"
                      v-model="tempFilter.procedure_ids" />
                    <label :for="option.id" class="ma-0">{{ option.name }}</label>
                  </div>
                </div>
                <v-divider color="#D7D7D7" class="my-3"></v-divider>
                <div class="d-flex action justify-content-between mt-2">
                  <CButton @click.native="clearAdditionalFilter" :outline="true" :color="'#007BFF'" label="Clear">
                  </CButton>
                  <CButton @click.native="applyFilter" :color="'#007BFF'" label="Apply">
                  </CButton>
                </div>
              </div>
            </v-menu>
          </div>
        </div>

        <!-- Only render column-child if group.data exists and is an array or object -->
        <div class="column-child py-2 px-4" v-if="group.data && (isKeyValue(group.data) || Array.isArray(group.data))">
          <p v-if="reportTemplate.type === 'Report::Client' && group.key === 'case_detail'" class="ma-0 mb-2"
            style="font-size: 14px;">FastVisa retrieves the <b>latest priority date</b> associated with the specific
            client.</p>
          <p v-if="reportTemplate.type === 'Report::Case' && group.key === 'client_detail'" class="ma-0 mb-2"
            style="font-size: 14px;">FastVisa retrieves the <b>latest priority date</b> from the <b>Priority Date
              module</b>
            within the case.</p>
          <p v-if="reportTemplate.type === 'Report::Join' && group.key === 'client_detail'" class="ma-0 mb-2"
            style="font-size: 14px;">FastVisa retrieves the <b>latest priority date</b> associated with the specific
            client.</p>
          <template v-if="group.key === 'Questionnaire::Template'">
            <v-select dense placeholder="Select Questionnaire" hide-details solo flat clearable
              class="input-border ml-6" :items="group.data" @change="onQuestionnaireChange"
              v-model="selectedQuestionnaire" item-value="id" return-object>
              <template v-slot:selection="{ item }">
                <span style="color: #3F3F3F !important;">#{{ item.id }} {{ item.name }}</span>
              </template>
              <template v-slot:item="{ item }">
                <span>#{{ item.id }} {{ item.name }}</span>
              </template>
            </v-select>
            <p class="ma-0 mt-2 ml-6" style="font-size: 14px;">The data displayed for the preview of this survey
              questionnaire is
              sample data. It will not be exported.</p>
          </template>

          <template v-else-if="isKeyValue(group.data)">
            <div v-for="(header_name, columnName) in group.data" :key="group.key + '_' + columnName"
              class="column-item d-flex align-items-center mb-2">
              <div class="custom-control custom-checkbox">
                <input type="checkbox" :id="'checkColumn_' + group.key + '_' + columnName"
                  :checked="selectedColumns.some(col => col.group === group.key && col.columnName === columnName)"
                  class="custom-control-input" @change="toggleColumnSelection(columnName, group.key, header_name)" />
                <label class="custom-control-label" :for="'checkColumn_' + group.key + '_' + columnName">
                  {{ header_name }}
                </label>
              </div>
            </div>
          </template>

          <template v-else>
            <div v-for="item in group.data" :key="group.key + '_' + item.id"
              class="column-item d-flex align-items-center mb-2">
              <div class="custom-control custom-checkbox">
                <input type="checkbox" :id="'checkColumn_' + group.key + '_' + item.id"
                  :checked="selectedColumns.some(col => col.group === group.key && col.columnName === item.id)"
                  class="custom-control-input"
                  @change="toggleColumnSelection(item.id, group.key, item.name || item.header_name || item.title || item.label)" />

                <label v-if="['Label::Case', 'Label::Ticket', 'Label::User'].some(label => group.key.includes(label))"
                  class="custom-control-label sticker" :for="'checkColumn_' + group.key + '_' + item.id"
                  :style="{ backgroundColor: item.color }">
                  {{ item.name }}
                </label>

                <label v-else class="custom-control-label" :for="'checkColumn_' + group.key + '_' + item.id">
                  {{ item.name || item.header_name || item.title || item.label }}
                  <v-tooltip v-if="group.key === 'Task::Dictionary' && item.procedure_count > 0" bottom
                    content-class="tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="{ ...on, mouseover: () => fetchProcedures(item) }" class="font-italic"
                        style="color: #007BFF; font-size: 12px;">Used in {{
                          item.procedure_count }} procedure(s)</span>
                    </template>
                    <span style="background-color: white;">
                      <b class="ma-0">Procedure(s)</b>
                      <ul class="mt-2 ma-0">
                        <li v-if="item.isFetchingProcedure" class="ma-0">Loading...</li>
                        <template v-else-if="Array.isArray(item.procedures) && item.procedures.length > 0">
                          <li class="ma-0" v-for="proc in item.procedures" :key="proc.id">
                            {{ proc.name }}
                          </li>
                        </template>
                        <li v-else class="ma-0">No procedures found</li>
                      </ul>
                    </span>
                  </v-tooltip>
                  <span v-else-if="group.key === 'Task::Dictionary' && item.procedure_count > 0" class="font-italic"
                    style="color: #007BFF; font-size: 12px;">Used
                    in {{
                      item.procedure_count }}
                    procedure(s)</span>
                  <i v-else-if="group.key === 'Task::Dictionary'" style="color: #6c757d; font-size: 12px;">
                    Not yet used in Procedure.
                  </i>
                </label>
              </div>
            </div>
          </template>
        </div>
        <p v-if="group && group.data && group.data.length === 0"
          class="d-flex justify-content-center align-items-center h-100">
          No Columns Available</p>
      </div>
      <p v-if="reportColumns.length === 0" class="d-flex justify-content-center align-items-center h-100">No Columns
        Available
      </p>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState, mapGetters } from 'vuex';
import CButton from '../../c-button.vue'
import CSelectNew from '../../c-select-new.vue';

export default {
  components: {
    CButton,
    CSelectNew,
  },
  data() {
    return {
      tempFilter: {
        procedure_ids: ['all'],
        sort: "newest",
      },
      sortOptions: [
        { text: 'Latest', value: 'newest' },
        { text: 'Oldest', value: 'oldest' },
        { text: 'Ascending', value: 'title_asc' },
        { text: 'Descending', value: 'title_desc' },
      ],
      selectedQuestionnaire: null,
      isFetchingProcedure: false,
    }
  },
  computed: {
    ...mapState({
      selectedColumns: (state) => state.reportTemplates.selectedColumns
    }),
    ...mapGetters({
      hasUnsavedChanges: "reportTemplates/hasUnsavedChanges",
      reportColumns: "reportTemplates/reportColumns",
      allReportFields: "reportTemplates/allReportFields",
      fieldPagination: 'reportTemplates/fieldPagination',
      prodecureListFilter: 'reportTemplates/prodecureListFilter',
      reportTemplate: 'reportTemplates/reportTemplate',
      previewDataIds: 'reportTemplates/previewDataIds',
    }),
    filteredReportColumns() {
      if (this.reportTemplate?.type === 'Report::Join') {
        return this.reportColumns.filter(group => group.key !== 'case_business_unit');
      }
      return this.reportColumns;
    },
    modifiedProcedureListFilter() {
      const allOption = { id: 'all', name: 'All' };
      const noProcedureOption = { id: 'no_procedure', name: 'Not Used' };
      return [allOption, noProcedureOption, ...this.prodecureListFilter];
    },
    groupedReportColumns() {
      const caseColumns = this.reportColumns.filter(col => col.source === 'case');
      const clientColumns = this.reportColumns.filter(col => col.source === 'client');

      return [
        { title: 'Case Column', columns: caseColumns },
        { title: 'Client Column', columns: clientColumns }
      ].filter(group => group.columns.length > 0);
    },
  },
  methods: {
    ...mapMutations({
      setFieldPagination: 'reportTemplates/setFieldPagination',
    }),
    async onQuestionnaireChange(selectedItem) {
      if (!selectedItem) {
        this.selectedQuestionnaire = null;

        let updatedColumns = this.selectedColumns.filter(col => col.group !== 'Questionnaire::Template');
        const perPage = this.fieldPagination.per_page;
        const totalCount = updatedColumns.length;
        const totalPages = Math.ceil(totalCount / perPage);
        let currentPage = this.fieldPagination.current_page;

        if (currentPage > totalPages) {
          currentPage = totalPages;
        }

        this.setFieldPagination({
          current_page: currentPage,
          per_page: perPage,
          total_count: totalCount,
          total_pages: totalPages,
          count: perPage,
        });

        this.$store.commit('reportTemplates/setSelectedColumns', updatedColumns);
        this.$store.commit('reportTemplates/setUnsavedChanges', this.hasUnsavedChanges);
        return;
      }

      let currentColumns = [...this.selectedColumns];

      const exists = currentColumns.some(col => col.group === 'Questionnaire::Template' && col.columnName == selectedItem.id);
      if (exists) return;

      let updatedColumns = currentColumns.filter(col => col.group !== 'Questionnaire::Template');

      const groupId = `questionnaire_${selectedItem.id}`;
      const newColumn = {
        columnName: selectedItem.id,
        group: 'Questionnaire::Template',
        position: updatedColumns.length + 1,
        header_name: selectedItem.name,
        original_header_name: selectedItem.name,
        data: [],
        isQuestionnaire: true,
      };

      updatedColumns.push(newColumn);

      updatedColumns = updatedColumns.map((col, index) => ({
        ...col,
        position: index + 1,
      }));

      const perPage = this.fieldPagination.per_page;
      const totalCount = updatedColumns.length;
      const totalPages = Math.ceil(totalCount / perPage);

      let currentPage = this.fieldPagination.current_page;

      if (currentPage > totalPages) {
        currentPage = totalPages;
      }

      this.setFieldPagination({
        current_page: currentPage,
        per_page: perPage,
        total_count: totalCount,
        total_pages: totalPages,
        count: perPage,
      });

      if (JSON.stringify(updatedColumns) !== JSON.stringify(this.selectedColumns)) {
        this.$store.commit('reportTemplates/setSelectedColumns', updatedColumns);
        this.$store.commit('reportTemplates/setUnsavedChanges', this.hasUnsavedChanges);
      }

      const payload = {
        id: this.reportTemplate.id,
        payload: {
          report_template: {
            filter: {
              template_ids: [selectedItem.id],
            },
          }
        }
      };

      this.$store.dispatch('reportTemplates/updateReportTemplate', payload);

      setTimeout(async () => {
        try {
          await this.$store.dispatch('reportTemplates/getPreviewDataIds', { id: this.reportTemplate.id });

          if (this.previewDataIds && Array.isArray(this.previewDataIds)) {
            const previewData = await this.$store.dispatch('reportTemplates/getColumnPreview', {
              templateId: this.reportTemplate.id,
              fieldId: null,
              column: {
                columnName: selectedItem.id,
                group: 'Questionnaire::Template',
              },
              previewDataIds: this.previewDataIds
            });

            if (previewData && Array.isArray(previewData)) {
              const updatedColumns = this.selectedColumns.map(col => {
                if (col.columnName === selectedItem.id && col.group === 'Questionnaire::Template') {
                  return {
                    ...col,
                    data: previewData.map(item => ({
                      groupId,
                      columnName: item.header.toLowerCase().replace(/\s+/g, '_'),
                      header_name: item.header,
                      original_header_name: item.header,
                      previewData: item.data || [],
                    }))
                  };
                }
                return col;
              });

              this.$store.commit('reportTemplates/setSelectedColumns', updatedColumns);
            }
          }
        } catch (error) {
          console.error("Failed to fetch preview data IDs or column preview:", error);
        }
      }, 500);
    },
    isGroupSelected(group) {
      const groupIds = this.getGroupIds(group);
      return groupIds.every(id =>
        this.selectedColumns.some(col => col.group === group.key && col.columnName === id)
      );
    },
    toggleGroupSelection(group) {
      if (!group.data || (!this.isKeyValue(group.data) && !Array.isArray(group.data))) {
        console.warn(`Invalid or undefined data for group: ${group.key}`);
        return;
      }

      const groupIds = this.getGroupIds(group);
      const isSelected = this.isGroupSelected(group);

      let updatedColumns;

      if (isSelected) {
        updatedColumns = this.selectedColumns.filter(
          col => !(col.group === group.key && groupIds.includes(col.columnName))
        );
      } else {
        const newColumns = groupIds
          .filter(id => !this.selectedColumns.some(col => col.group === group.key && col.columnName === id))
          .map(id => {
            let headerName = id;

            if (Array.isArray(group.data)) {
              const field = group.data.find(item => item.id === id);
              headerName = field.title || field.name || field.label || id;
            } else if (typeof group.data === "object" && group.data !== null) {
              headerName = group.data[id] || id;
            }

            return {
              group: group.key,
              columnName: id,
              header_name: headerName,
              original_header_name: headerName,
              position: this.allReportFields.find(field => field.column_name === id)?.position || 0,
              ...(group.source ? { source: group.source } : {})
            };
          });
        updatedColumns = [...this.selectedColumns, ...newColumns];
      }

      updatedColumns = updatedColumns.map((col, index) => ({
        ...col,
        position: index + 1,
      }));

      const perPage = this.fieldPagination.per_page;
      const totalCount = updatedColumns.length;
      const totalPages = Math.ceil(totalCount / perPage);

      let currentPage = this.fieldPagination.current_page;

      if (!isSelected) {
        currentPage = totalPages;
      } else {
        if (currentPage > totalPages) {
          currentPage = totalPages;
        }
      }

      this.setFieldPagination({
        current_page: currentPage,
        per_page: perPage,
        total_count: totalCount,
        total_pages: totalPages,
        count: perPage,
      });

      this.$store.commit('reportTemplates/setSelectedColumns', updatedColumns);
      this.$store.commit('reportTemplates/setUnsavedChanges', this.hasUnsavedChanges);
    },
    toggleColumnSelection(columnId, groupKey, header_name) {
      const columnIndex = this.selectedColumns.findIndex(
        col => col.group === groupKey && col.columnName === columnId
      );

      let updatedColumns;

      if (columnIndex > -1) {
        updatedColumns = this.selectedColumns.filter(
          col => !(col.group === groupKey && col.columnName === columnId)
        );
      } else {
        updatedColumns = [
          ...this.selectedColumns,
          {
            group: groupKey,
            columnName: columnId,
            header_name,
            original_header_name: header_name,
            position: this.selectedColumns.length + 1,
            ...(this.getGroupSource(groupKey) ? { source: this.getGroupSource(groupKey) } : {})
          },
        ];
      }

      updatedColumns = updatedColumns.map((col, index) => ({
        ...col,
        position: index + 1,
      }));

      const perPage = this.fieldPagination.per_page;
      const totalCount = updatedColumns.length;
      const totalPages = Math.ceil(totalCount / perPage);

      let currentPage = this.fieldPagination.current_page;

      if (columnIndex === -1) {
        currentPage = totalPages;
      } else {
        if (currentPage > totalPages) {
          currentPage = totalPages;
        }
      }

      this.setFieldPagination({
        current_page: currentPage,
        per_page: perPage,
        total_count: totalCount,
        total_pages: totalPages,
        count: perPage,
      });

      this.$store.commit('reportTemplates/setSelectedColumns', updatedColumns);
      this.$store.commit('reportTemplates/setUnsavedChanges', this.hasUnsavedChanges);
    },
    getGroupIds(group) {
      if (!group.data) {
        console.warn(`Group data is undefined for group: ${group.key}`);
        return []; // Return an empty array if group.data is undefined
      }

      if (this.isKeyValue(group.data)) {
        return Object.keys(group.data);
      } else if (Array.isArray(group.data)) {
        return group.data.map(item => item.id);
      }

      console.warn(`Unexpected data format for group: ${group.key}`);
      return []; // Return an empty array for unexpected data formats
    },
    getGroupSource(groupKey) {
      const group = this.reportColumns.find(g => g.key === groupKey)
      return group ? group.source : null;
    },
    isKeyValue(data) {
      return typeof data === 'object' && !Array.isArray(data);
    },
    checkAll(value) {
      if (value === 'all') {
        this.tempFilter.procedure_ids = this.modifiedProcedureListFilter
          .filter(option => option.id !== 'all')
          .map(option => option.id);
      } else {
        this.tempFilter.procedure_ids = this.tempFilter.procedure_ids.filter(item => item !== 'all');

        const allOptionsSelected = this.modifiedProcedureListFilter
          .filter(option => option.id !== 'all')
          .every(option => this.tempFilter.procedure_ids.includes(option.id));

        if (allOptionsSelected) {
          this.tempFilter.procedure_ids.push('all');
        }
      }

      if (this.tempFilter.procedure_ids.length === 0) {
        this.tempFilter.procedure_ids = ['all'];
      }
    },
    clearAdditionalFilter() {
      this.tempFilter = {
        procedure_ids: ['all'],
        sort: "",
      };
      this.applyFilter();
    },
    async applyFilter() {
      let filter = {
        ...this.tempFilter,
      };

      if (filter.procedure_ids.includes('all')) {
        filter.procedure_ids = [];
      }

      // action
      await this.$store.dispatch('reportTemplates/getTaskDictionary', { params: filter });
    },
    async fetchProcedures(item) {
      if (!item.hasOwnProperty('isFetchingProcedure')) {
        this.$set(item, 'isFetchingProcedure', false);
      }
      if (!item.hasOwnProperty('procedures')) {
        this.$set(item, 'procedures', []);
      }

      if (item.isFetchingProcedure || (Array.isArray(item.procedures) && item.procedures.length > 0)) {
        return;
      }

      this.$set(item, 'isFetchingProcedure', true);

      try {
        const response = await this.$store.dispatch('reportTemplates/getTaskDictionaryById', item.id);

        this.$set(item, 'procedures', Array.isArray(response) ? response : []);
      } catch (error) {
        console.error('Error fetching procedures:', error);
        this.$set(item, 'procedures', []);
      } finally {
        this.$set(item, 'isFetchingProcedure', false);
      }
    },
    getGroupLabel(group) {
      if (this.isClientBusinessDepartmentGroup(group)) {
        // Normalize to a single "(Client)" suffix
        const baseName = String(group.name || '').replace(/\s*\(Client\)/gi, '').trim();
        return `${baseName} (Client)`;
      }
      return group.name;
    },
    isClientBusinessDepartmentGroup(group) {
      // Only add "(Client)" when the report itself is not a Client report, but the BU/Dept fields
      // are coming from the client context (Case + Join reports).
      if (this.reportTemplate?.type === 'Report::Client') return false;
      return ['business_unit', 'client_business_unit'].includes(group?.key);
    },
  },
  watch: {
    selectedColumns: {
      handler(newVal) {
        if (!newVal || newVal.length === 0) return;
        const existingQuestionnaire = newVal.find(col => col.group === 'Questionnaire::Template');

        if (existingQuestionnaire) {
          this.selectedQuestionnaire = {
            id: existingQuestionnaire.columnName,
            name: existingQuestionnaire.header_name,
          };

          this.onQuestionnaireChange(this.selectedQuestionnaire);
        }
      },
      immediate: true,
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
.column-header {
  background-color: #F4F4F4;
}

.column-group {
  margin-bottom: 16px;
}

.column-group:last-child {
  margin-bottom: 8px;
}

.scroll-container {
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 0 16px;
}

.sticker {
  padding: 8px 16px;
  border-radius: 4px;
}

::v-deep .custom-control-label.sticker::before,
.custom-control-label.sticker::after {
  top: 0.7rem !important;
}

::v-deep .v-select__selections {
  gap: 4px !important;
}

::v-deep .custom-control-label[disabled],
.custom-control-label:disabled,
.custom-control-label {
  color: #3F3F3F !important;
}
</style>
